import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useLocation, Link, useNavigate } from 'react-router-dom';
import { Box, Text, Checkbox, Tooltip, Button } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';

import { useApi } from '../../../../../context/ApiProvider';
import { KidInterface } from '../../../../../shared/type/kid.type';
import CustomBadge from '../../../../CustomBadge/CustomBadge';
import IconComponent from '../../../../IconComponent/IconComponent';
import { StickyScrollableTableContainer } from '../../../../DataTable/StickyScrollableTableContainer';
import CustomChakraButton from '../../../../CustomChakraButton/CustomChakraButton';
import AddKid from './AddKid/AddKid';
import MoveKid from './MoveKid/MoveKid';
import AddOrder from './AddOrder/AddOrder';
import ResetSaldo from './ResetSaldo/ResetSaldo';
import DeleteKid from './DeleteKid/DeleteKid';
import ImportParentsFromFilePopUp from '../../Parents/ParentsTable/ImportParentsFromFilePopUp/ImportParentsFromFilePopUp';
import { MassEditChildren } from './MassEditChildren/MassEditChildren';
import { AxiosResponse } from 'axios';
import { OrganisationUnitInterface } from '../../../../../shared';
import useAuth from '../../../../../hooks/useAuth';
import CreateNewAdminThread from '../../../MailBox/CreateNewAdminThread/CreateNewAdminThread';

const ChildrenTable = () => {
  const { apiTenantConsumerController, apiTenantOrganisationUnitController } = useApi();
  const navigate = useNavigate();
  const { id: tenantId } = useParams();
  const { auth } = useAuth();
  const location = useLocation();
  const [children, setChildren] = useState<KidInterface[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedKidIds, setSelectedKidIds] = useState<number[]>([]);
  const [visibleChildren, setVisibleChildren] = useState<KidInterface[]>([]);
  const columnHelper = createColumnHelper<KidInterface>();
  const [showEditStackPopUp, setShowEditStackPopUp] = useState<boolean>(false);
  const [classes, setClasses] = useState<{ label: string; value: number }[]>([]);
  const [sendMessagePopUp, setSendMessagePopUp] = useState({ show: false, userId: 0 });
  const [popUp, setPopUp] = useState<{
    deleteKid: boolean;
    moveKid: boolean;
    resetSaldo: boolean;
    addKid: boolean;
    addOrder: boolean;
    importKids: boolean;
  }>({
    deleteKid: false,
    moveKid: false,
    resetSaldo: false,
    addKid: false,
    addOrder: false,
    importKids: false
  });

  const handleChangePopUpState = (
    name: 'deleteKid' | 'addKid' | 'moveKid' | 'resetSaldo' | 'addOrder' | 'importKids',
    value: boolean
  ) => {
    setPopUp((prev) => ({ ...prev, [name]: value }));
  };

  const clearingSelectedKidIds = useCallback(() => {
    setSelectedKidIds([]);
  }, [selectedKidIds]);

  const handleCheckboxChange = (kidId: number) => {
    setSelectedKidIds((prevSelected) =>
      prevSelected.includes(kidId)
        ? prevSelected.filter((id) => id !== kidId)
        : [...prevSelected, kidId]
    );
  };

  const handleSelectAll = (isChecked: boolean) => {
    if (isChecked) {
      setSelectedKidIds(visibleChildren.map((child) => child.id));
    } else {
      setSelectedKidIds([]);
    }
  };

  const columns = [
    columnHelper.display({
      id: 'select',
      header: () => (
        <Checkbox
          colorScheme="customOrange"
          isChecked={selectedKidIds.length === visibleChildren.length && visibleChildren.length > 0}
          isDisabled={children.length === 0}
          onChange={(e) => {
            return handleSelectAll(e.target.checked);
          }}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          colorScheme="customOrange"
          isChecked={selectedKidIds.includes(row.original.id)}
          onChange={() => handleCheckboxChange(row.original.id)}
        />
      ),
      size: 40
    }),
    columnHelper.display({
      id: 'rowNumber',
      cell: (info) => <span>{info.row.index + 1}.</span>,
      header: 'Lp.',
      size: 40,
      meta: { isNumeric: true }
    }),
    columnHelper.accessor('lastName', {
      cell: (info) => {
        const { lastName, firstName, id } = info.row.original;
        const newPath = location.pathname.replace('children-table', `single-kid/${id}/orders`);
        return (
          <p className="text-sm font-medium text-grayLight-900 underline underline-offset-4">
            <Link to={newPath}>
              {lastName} {firstName}
            </Link>
          </p>
        );
      },
      header: 'Nazwisko i imię',
      size: 180
    }),
    columnHelper.accessor('organisationUnitName', {
      cell: (info) => (
        <p className="text-sm font-medium text-grayLight-900">{info.getValue() || '-'}</p>
      ),
      header: 'Klasa',
      size: 100
    }),
    columnHelper.accessor('saldo', {
      cell: (info) => {
        const saldo = info.getValue();
        const isNegative = saldo.includes('-');
        return (
          <CustomBadge color={isNegative ? 'error' : 'success'} size="sm">
            {saldo}
          </CustomBadge>
        );
      },
      header: 'Saldo',
      size: 100
    }),
    columnHelper.accessor('cardNumber', {
      cell: (info) => <p className="text-sm text-grayLight-900">{info.getValue() || '-'}</p>,
      header: 'Karta/Brelok',
      size: 120
    }),
    columnHelper.accessor('parents', {
      cell: (info) => {
        const parents = info.getValue();
        const newPath = (parentId: number) =>
          location.pathname.replace(
            'children/children-table',
            `parents/single-parent/${parentId}/list-of-orders`
          );

        if (parents.length === 0) return <p>-</p>;

        return (
          <ul className="flex flex-col">
            {parents.map((parent) => (
              <li
                key={parent.id}
                className="text-sm font-medium text-grayLight-900 underline underline-offset-4 pt-1 pb-1">
                <Link to={newPath(parent.id)}>
                  {parent.lastName} {parent.firstName}
                </Link>
                <button
                  onClick={() => setSendMessagePopUp({ show: true, userId: parent.id })}
                  className="w-4 h-4 ml-1 inline align-middle">
                  <IconComponent iconName="message" color="#475467" />
                </button>
              </li>
            ))}
          </ul>
        );
      },
      header: 'Rodzic/e',
      size: 160
    }),
    columnHelper.accessor('cofinancingList', {
      cell: (info) => {
        const list = info.getValue();
        if (list.length === 0) return <p>-</p>;
        return (
          <ul className="flex flex-col">
            {list.map((item) => (
              <li
                key={item.id}
                className="text-sm font-medium text-grayLight-900 underline underline-offset-4 pt-1 pb-1">
                {item.cofinancingInstitutionName}
              </li>
            ))}
          </ul>
        );
      },
      header: 'Dofinansowania',
      size: 160
    }),
    columnHelper.accessor('remarks', {
      cell: (info) => {
        const remarks = info.getValue();
        return (
          <div className="flex items-center">
            {remarks ? (
              <Tooltip
                label={remarks}
                hasArrow
                placement="top"
                bg="#fff7e6"
                textColor="#101828"
                fontSize="md">
                <button>
                  <IconComponent iconName="annotation-alert" />
                </button>
              </Tooltip>
            ) : (
              <p>-</p>
            )}
          </div>
        );
      },
      header: 'Uwagi',
      size: 80
    })
  ];

  const fetchChildren = async () => {
    setIsLoading(true);
    try {
      const response = await apiTenantConsumerController('list-data').post('', {
        tenantId: Number(tenantId),
        searchPhrase: ''
      });
      setChildren(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOrganizationUnits = async () => {
    try {
      const response: AxiosResponse<OrganisationUnitInterface[]> =
        await apiTenantOrganisationUnitController('').get(`/${tenantId}`);
      const classes = response.data.map((organisation) => ({
        label: organisation.name,
        value: organisation.id
      }));

      setClasses(classes);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (tenantId === '0' || tenantId === 'undefined') return;
    setIsLoading(true);
    fetchChildren();
    fetchOrganizationUnits();
  }, [tenantId]);

  return (
    <section className="ChildrenTable p-spacing-xl w-full">
      <Box mb={6} className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <Text fontSize="xl" fontWeight="semibold" color="gray.900">
            Uczniowie
          </Text>
          <Text fontSize="sm" color="gray.600">
            Lista uczniów przypisanych do oddziału
          </Text>
        </div>
        <div className="flex flex-wrap gap-2">
          <CustomChakraButton
            size="md"
            hierarchy="linkGray"
            iconPosition="left"
            icon="import"
            buttonProps={{
              onClick: () => {
                handleChangePopUpState('importKids', true);
              }
            }}>
            Importuj listę
          </CustomChakraButton>
          <CustomChakraButton
            size="md"
            hierarchy="linkGray"
            iconPosition="left"
            icon="clock"
            buttonProps={{
              onClick: () => {
                if (auth.loggedUserDto.superAdmin) {
                  navigate(`/superAdmin/branches/id/${tenantId}/users/children/archive-children`);
                } else {
                  navigate(`/tenantAdmin/id/${tenantId}/users/children/archive-children`);
                }
              }
            }}>
            Archiwum
          </CustomChakraButton>
          <CustomChakraButton
            size="md"
            hierarchy="primary"
            iconPosition="left"
            icon="plus"
            buttonProps={{
              onClick: () => {
                handleChangePopUpState('addKid', true);
              }
            }}>
            Dodaj ucznia
          </CustomChakraButton>
        </div>
      </Box>

      <StickyScrollableTableContainer
        searchStorageKey="users-children-search"
        sortingStorageKey="users-children-sorting"
        data={children}
        columns={columns}
        isLoading={isLoading}
        enableSearch={true}
        searchPlaceholder="Szukaj ucznia..."
        onVisibleRowsChange={(rows) => setVisibleChildren(rows)}
        filtersRightContent={
          <div className="flex flex-wrap gap-2">
            <CustomChakraButton
              size="sm"
              hierarchy="tertiaryColor"
              iconPosition="onlyText"
              buttonProps={{
                onClick: () => {
                  handleChangePopUpState('addOrder', true);
                },
                disabled: selectedKidIds.length === 0
              }}>
              Dodaj zamówienie saldo/kredyt
            </CustomChakraButton>
            <CustomChakraButton
              size="sm"
              hierarchy="tertiaryColor"
              iconPosition="onlyText"
              buttonProps={{
                type: 'button',
                onClick: () => {
                  setShowEditStackPopUp(true);
                },
                disabled: selectedKidIds.length === 0
              }}>
              Edytuj
            </CustomChakraButton>
            <CustomChakraButton
              size="sm"
              hierarchy="tertiaryColor"
              iconPosition="onlyText"
              buttonProps={{
                onClick: () => {
                  handleChangePopUpState('moveKid', true);
                },
                disabled: selectedKidIds.length === 0
              }}>
              Przenieś do klasy
            </CustomChakraButton>
            <CustomChakraButton
              size="sm"
              hierarchy="tertiaryColor"
              iconPosition="onlyText"
              buttonProps={{
                onClick: () => {
                  handleChangePopUpState('resetSaldo', true);
                },
                disabled: selectedKidIds.length === 0
              }}>
              Wyzeruj saldo
            </CustomChakraButton>
            <CustomChakraButton
              size="sm"
              hierarchy="tertiaryColor"
              iconPosition="onlyText"
              buttonProps={{
                disabled: selectedKidIds.length === 0,
                onClick: () => handleChangePopUpState('deleteKid', true)
              }}>
              Usuń zaznaczone
            </CustomChakraButton>
          </div>
        }
      />
      {popUp.addKid && (
        <AddKid
          isOpen={popUp.addKid}
          handleClose={() => {
            handleChangePopUpState('addKid', false);
          }}
          afterAction={() => {
            clearingSelectedKidIds();
            fetchChildren();
          }}
        />
      )}
      {popUp.moveKid && (
        <MoveKid
          kidIds={selectedKidIds}
          isOpen={popUp.moveKid}
          handleClose={() => {
            handleChangePopUpState('moveKid', false);
          }}
          afterAction={() => {
            clearingSelectedKidIds();
            fetchChildren();
          }}
        />
      )}
      {popUp.addOrder && (
        <AddOrder
          isOpen={popUp.addOrder}
          consumerIds={selectedKidIds}
          handleClose={() => {
            handleChangePopUpState('addOrder', false);
          }}
          afterAction={() => {
            clearingSelectedKidIds();
            fetchChildren();
          }}
        />
      )}
      {popUp.resetSaldo && (
        <ResetSaldo
          isOpen={popUp.resetSaldo}
          consumerIds={selectedKidIds}
          handleClose={() => {
            handleChangePopUpState('addOrder', false);
          }}
          afterAction={() => {
            clearingSelectedKidIds();
            fetchChildren();
          }}
        />
      )}
      {popUp.deleteKid && (
        <DeleteKid
          isOpen={popUp.deleteKid}
          onConfirm={() => {
            handleChangePopUpState('deleteKid', false);
            fetchChildren();
          }}
          allChildren={children}
          selectedChildIds={selectedKidIds}
          setSelectedChildIds={setSelectedKidIds}
          afterAction={() => clearingSelectedKidIds()}
          onCancel={() => {
            handleChangePopUpState('deleteKid', false);
          }}
        />
      )}

      {popUp.importKids && (
        <ImportParentsFromFilePopUp
          isOpen={popUp.importKids}
          onClose={() => {
            handleChangePopUpState('importKids', false);
            fetchChildren();
          }}
        />
      )}
      {showEditStackPopUp ? (
        <MassEditChildren
          handleClose={() => {
            setShowEditStackPopUp(false);
          }}
          afterAction={() => fetchChildren()}
          isOpen={showEditStackPopUp}
          usersIds={selectedKidIds}
          classes={classes}
        />
      ) : null}

      {sendMessagePopUp.show && (
        <CreateNewAdminThread
          isOpen
          onClose={() => setSendMessagePopUp({ show: false, userId: 0 })}
          initialTargetUserId={sendMessagePopUp.userId}
        />
      )}
    </section>
  );
};

export default ChildrenTable;
